import {featureCommandAdapterRegistry} from '../command/FeatureCommandAdapterRegistry';
import type {FeaturePrototype} from '../prototype/FeaturePrototype';
import {featureDisplayNamePolicy, type FeatureDisplayNameContext} from './FeatureDisplayNamePolicy';
import {featureNodeEditPolicy, type FeatureNodeEditCapabilities} from './FeatureNodeEditPolicy';
import {
    createFeatureRollbackTimeline,
    FeatureRollbackTimelineItemKind,
    type FeatureRollbackProjectionState,
    type FeatureRollbackTimelineItem,
} from '../rollback';
import { collectCurrentFeatureResultObjectIds } from '../../utils/feature/featureCommandDependencyUtils';
import {
    createSketchIdentityIndex,
    normalizeSketchIdToRuntime,
} from '../../sketch/identity/sketchIdentityIndex';
import type {SketchEntityReferenceWarningSummary} from '../../../types/reference/sketch-entity-reference/SketchEntityReferenceDiagnosticProjection';

/**
 * 场景草图投影输入。
 */
export interface SceneSketchProjectionInput {
    /** 草图运行时 ID（场景句柄）。 */
    id: string;
    /**
     * 草图跨会话稳定持久 ID。
     * 特征绑定原生记录的是持久 ID，投影层据此把特征来源草图归一回场景句柄。
     */
    persistentId?: string | null;
    /** 草图实体类型。 */
    entityType: string;
    /** 草图名称。 */
    name?: string;
    /** 草图顺序。 */
    order: number;
    /** 草图创建时间戳（毫秒），用于设计树时间线排序。 */
    createdAt?: number;
    /**
     * 是否为当前正在编辑的草图。
     *
     * 严格前缀会把消费 Feature 标记为暂时回退，但被临时释放出来编辑的源草图
     * 本身仍然有效，不能继承消费 Feature 的置灰状态。
     */
    editing?: boolean;
    /** 转换实体引用悬空警告摘要。 */
    referenceWarning?: SketchEntityReferenceWarningSummary;
}

/**
 * 用户基准面投影输入。
 */
export interface SceneDatumPlaneProjectionInput {
    /** 基准面 ID（十进制字符串，与退回时间线一致）。 */
    id: string;
    /** 显示名称。 */
    name: string;
    /** FeatureManager 统一顺序刻度。 */
    order: number;
    /** 是否隐藏。 */
    hidden: boolean;
}

/**
 * 用户基准轴投影输入。
 */
export interface SceneDatumAxisProjectionInput {
    /** 基准轴 ID（十进制字符串，与退回时间线一致）。 */
    id: string;
    /** 显示名称。 */
    name: string;
    /** FeatureManager 统一顺序刻度。 */
    order: number;
    /** 是否隐藏。 */
    hidden: boolean;
}

/**
 * 用户基准点投影输入。
 */
export interface SceneDatumPointProjectionInput {
    /** 基准点 ID（十进制字符串，与退回时间线一致）。 */
    id: string;
    /** 显示名称。 */
    name: string;
    /** FeatureManager 统一顺序刻度。 */
    order: number;
    /** 是否隐藏。 */
    hidden: boolean;
}

/**
 * 特征投影节点。
 */
export interface FeatureProjectionNode {
    /** 特征 ID。 */
    id: string;
    /** 特征种类。 */
    kind: string;
    /** 显示名称。 */
    displayName: string;
    /** 历史顺序。 */
    order: number;
    /** 源草图 ID。 */
    sourceSketchId?: string;
    /** 编辑能力。 */
    editCapabilities: FeatureNodeEditCapabilities;
    /** 图标类型。 */
    iconType: string;
    /** 是否隐藏。 */
    hidden: boolean;
    /** 是否位于退回控制棒下方。 */
    rolledBack: boolean;
    /** 当前特征实际生成的结果体对象 ID 列表。 */
    bodyRefs: string[];
}

/**
 * 草图投影节点。
 */
export interface SketchProjectionNode {
    /** 草图 ID。 */
    id: string;
    /** 显示名称。 */
    displayName: string;
    /** 实体类型。 */
    entityType: string;
    /** 顺序。 */
    order: number;
    /** 是否隐藏。 */
    hidden: boolean;
    /** 是否压制。 */
    suppressed: boolean;
    /** 是否位于退回控制棒下方。 */
    rolledBack: boolean;
    /** 是否为当前正在编辑的草图。 */
    editing: boolean;
    /** 被哪个特征吸收。 */
    consumedByFeatureId?: string;
    /** 图标类型。 */
    iconType: string;
    /** 转换实体引用悬空警告摘要。 */
    referenceWarning?: SketchEntityReferenceWarningSummary;
}

/**
 * 用户基准面投影节点。
 */
export interface DatumPlaneProjectionNode {
    /** 基准面 ID（字符串）。 */
    id: string;
    /** 显示名称。 */
    displayName: string;
    /** FeatureManager 顺序。 */
    order: number;
    /** 是否隐藏。 */
    hidden: boolean;
    /** 是否位于退回控制棒下方。 */
    rolledBack: boolean;
    /** 图标类型。 */
    iconType: string;
}

/**
 * 用户基准轴投影节点。
 */
export interface DatumAxisProjectionNode {
    /** 基准轴 ID（字符串）。 */
    id: string;
    /** 显示名称。 */
    displayName: string;
    /** FeatureManager 顺序。 */
    order: number;
    /** 是否隐藏。 */
    hidden: boolean;
    /** 是否位于退回控制棒下方。 */
    rolledBack: boolean;
    /** 图标类型。 */
    iconType: string;
}

/**
 * 用户基准点投影节点。
 */
export interface DatumPointProjectionNode {
    /** 基准点 ID（字符串）。 */
    id: string;
    /** 显示名称。 */
    displayName: string;
    /** FeatureManager 顺序。 */
    order: number;
    /** 是否隐藏。 */
    hidden: boolean;
    /** 是否位于退回控制棒下方。 */
    rolledBack: boolean;
    /** 图标类型。 */
    iconType: string;
}

/**
 * 设计树投影文档。
 */
export interface FeatureProjectionDocument {
    /** 特征列表。 */
    features: FeatureProjectionNode[];
    /** 草图列表。 */
    sketches: SketchProjectionNode[];
    /** 用户基准面列表。 */
    datumPlanes: DatumPlaneProjectionNode[];
    /** 用户基准轴列表。 */
    datumAxes: DatumAxisProjectionNode[];
    /** 用户基准点列表。 */
    datumPoints: DatumPointProjectionNode[];
    /** 当前结果体摘要。 */
    currentResult: {
        /** 结果体引用。 */
        bodyRefs: string[];
        /** 权威结果体数量。 */
        authoritativeBodyCount: number;
    };
    /** 每个显示命名族的下一个显示序号。 */
    nextDisplayIndexByKind: Record<string, number>;
    /** 完整 FeatureManager 退回序列。 */
    rollbackTimelineItems: readonly FeatureRollbackTimelineItem[];
    /** 控制棒在 FeatureManager 退回序列中的插入索引。 */
    rollbackCursorIndex: number;
}

/**
 * 设计树渲染节点。
 */
export interface FeatureTreeProjectionNode {
    /** 节点键。 */
    key: string;
    /** 标签。 */
    label: string;
    /** 节点类型。 */
    nodeType: 'feature' | 'sketch' | 'datum-plane' | 'datum-axis' | 'datum-point' | 'rollback-bar';
    /** 图标类型。 */
    iconType: string;
    /** 引用对象 ID。 */
    refObjectId?: string;
    /** 当前特征实际生成的结果体对象 ID 列表。 */
    bodyRefs?: string[];
    /** 是否隐藏。 */
    hidden?: boolean;
    /** 是否位于退回控制棒下方。 */
    rolledBack?: boolean;
    /** 转换实体引用悬空警告摘要。 */
    referenceWarning?: SketchEntityReferenceWarningSummary;
    /** 当前特征在完整特征历史中的索引。 */
    featureCommandIndex?: number;
    /** 当前节点在完整 FeatureManager 退回序列中的索引。 */
    featureManagerIndex?: number;
    /** 退回控制棒插入索引。 */
    rollbackCursorIndex?: number;
    /**
     * 完整 FeatureManager 退回序列长度。
     * 仅退回控制棒节点携带，供宿主拖拽落点在渲染空洞时兜底推到时间线末尾。
     */
    totalTimelineItemCount?: number;
    /** 子节点。 */
    children?: FeatureTreeProjectionNode[];
}

/**
 * 设计树渲染模型。
 */
export interface FeatureTreeProjectionModel {
    /** 树节点集合。 */
    nodes: FeatureTreeProjectionNode[];
    /** 每个草图被渲染的节点数量。 */
    sketchNodeCountById: Map<string, number>;
}

/**
 * 设计树投影输入。
 */
export interface BuildFeatureProjectionDocumentInput {
    /** 命令历史。 */
    history: CadCommand[];
    /** 几何对象列表。 */
    objects: GeometryObject[];
    /** 场景草图列表。 */
    sceneSketches: SceneSketchProjectionInput[];
    /** 用户基准面列表；默认三基准面不得传入。 */
    sceneDatumPlanes?: SceneDatumPlaneProjectionInput[];
    /** 用户基准轴列表。 */
    sceneDatumAxes?: SceneDatumAxisProjectionInput[];
    /** 用户基准点列表；原点不得传入。 */
    sceneDatumPoints?: SceneDatumPointProjectionInput[];
    /** 隐藏对象集合。 */
    hiddenObjectIds: Set<string>;
    /** 压制草图集合。 */
    suppressedSketchIds: Set<string>;
    /** 名称覆盖表。 */
    objectNameOverrides: Map<string, string>;
    /** 退回控制棒投影状态。 */
    rollbackState?: FeatureRollbackProjectionState | null;
}

/**
 * 设计树文档输入。
 * 说明：
 * 1. 该命名与 web-client 现有消费入口保持一致；
 * 2. 仅作为 projection 公共包装的类型别名，不新增额外语义；
 * 3. 宿主层后续迁移到 cad-core 公共投影 API 时，可直接复用该名称。
 */
export type BuildPartFeatureDocumentInput = BuildFeatureProjectionDocumentInput;

/**
 * 场景草图节点输入。
 * 说明：
 * 1. 该命名与 web-client 现有设计树投影入口保持一致；
 * 2. 仅作为 projection 公共包装别名；
 * 3. 避免宿主层为同一语义重复定义本地类型。
 */
export type SceneSketchNodeInput = SceneSketchProjectionInput;

/**
 * 设计树文档。
 * 说明：
 * 1. 该类型为 FeatureProjectionDocument 的公共包装别名；
 * 2. 通过保持旧命名，减少宿主层迁移成本；
 * 3. 真正的语义真相仍由 cad-core projection 层维护。
 */
export type PartFeatureDocument = FeatureProjectionDocument;

/**
 * 设计树渲染节点。
 * 说明：
 * 1. 该类型为 FeatureTreeProjectionNode 的公共包装别名；
 * 2. 宿主层使用旧命名时，不需要再本地复制结构定义；
 * 3. 所有节点语义仍以 cad-core projection 输出为准。
 */
export type FeatureTreeRenderNode = FeatureTreeProjectionNode;

/**
 * 设计树渲染模型。
 * 说明：
 * 1. 该类型为 FeatureTreeProjectionModel 的公共包装别名；
 * 2. 用于为 web-client 提供稳定的公共投影入口；
 * 3. 避免宿主层继续维护本地版本的树模型类型。
 */
export type FeatureTreeRenderModel = FeatureTreeProjectionModel;

/**
 * 设计树投影构建器。
 */
export class FeatureTreeProjectionBuilder {
    /**
     * 构建设计树投影文档。
     * @param input 投影输入。
     * @returns 投影文档。
     */
    public buildDocument(input: BuildFeatureProjectionDocumentInput): FeatureProjectionDocument {
        const sceneSketches = [...input.sceneSketches].sort((a, b) => a.order - b.order);
        /**
         * 草图身份归一索引。
         *
         * 特征命令与特征绑定原生记录的是草图持久 ID，而场景草图、显示态、设计树节点句柄一律是运行时 uuid。
         * 投影层是这两个 ID 空间唯一的交汇点，必须在入口统一换算：
         * 一旦漏换算，被特征吸收的草图会因匹配不上而退化成与特征同级的顶级节点。
         */
        const sketchIdentityIndex = createSketchIdentityIndex(
            sceneSketches.map((sketch) => ({
                runtimeId: sketch.id,
                persistentId: sketch.persistentId,
            })),
        );
        const sceneDatumPlanes = [...(input.sceneDatumPlanes ?? [])].sort((a, b) => a.order - b.order);
        const sceneDatumAxes = [...(input.sceneDatumAxes ?? [])].sort((a, b) => a.order - b.order);
        const sceneDatumPoints = [...(input.sceneDatumPoints ?? [])].sort((a, b) => a.order - b.order);
        const nextFeatureIndexByDisplayFamily: Record<string, number> = {};
        const features: FeatureProjectionNode[] = [];

        const rolledBackFeatureIds = new Set(input.rollbackState?.rolledBackFeatureIds ?? []);
        const rolledBackSketchIds = new Set(input.rollbackState?.rolledBackSketchIds ?? []);
        const rolledBackDatumPlaneIds = new Set(input.rollbackState?.rolledBackDatumPlaneIds ?? []);
        const rolledBackDatumAxisIds = new Set(input.rollbackState?.rolledBackDatumAxisIds ?? []);
        const rolledBackDatumPointIds = new Set(input.rollbackState?.rolledBackDatumPointIds ?? []);
        const authoritativeBodies = input.objects
            .filter((object) => object.type === 'solid' && !this.isEphemeralPreviewObject(object));

        input.history.forEach((command, historyIndex) => {
            const prototype = featureCommandAdapterRegistry.createPrototypeFromCommand(command);
            if (!prototype) {
                return;
            }

            const featureKind = prototype.getFeatureKind();
            const displayNameContext = this.resolveFeatureDisplayNameContext(prototype);
            const externalOverrideName = input.objectNameOverrides.get(command.id);
            const overrideName = typeof externalOverrideName === 'string' && externalOverrideName.trim().length > 0
                ? externalOverrideName
                : this.resolveFeatureCommandName(prototype);
            const displayNameFamily = featureDisplayNamePolicy.resolveFeatureDisplayNameFamily(
                featureKind,
                displayNameContext,
            );
            const nextFeatureIndex = nextFeatureIndexByDisplayFamily[displayNameFamily.key] ?? 1;
            features.push({
                id: command.id,
                kind: featureKind,
                displayName: featureDisplayNamePolicy.resolveFeatureDisplayName(
                    featureKind,
                    nextFeatureIndex,
                    overrideName,
                    displayNameContext,
                ),
                order: historyIndex,
                sourceSketchId: normalizeSketchIdToRuntime(
                    sketchIdentityIndex,
                    prototype.getSourceSketchId(),
                ) ?? undefined,
                editCapabilities: featureNodeEditPolicy.resolve(featureKind),
                iconType: this.resolveFeatureIconType(featureKind),
                hidden: input.hiddenObjectIds.has(command.id),
                rolledBack: rolledBackFeatureIds.has(command.id),
                bodyRefs: collectCurrentFeatureResultObjectIds(command.id, authoritativeBodies),
            });
            nextFeatureIndexByDisplayFamily[displayNameFamily.key] = nextFeatureIndex + 1;
        });

        const consumingFeatureBySketchId = new Map<string, string>();
        features.forEach((feature) => {
            if (feature.sourceSketchId && !consumingFeatureBySketchId.has(feature.sourceSketchId)) {
                consumingFeatureBySketchId.set(feature.sourceSketchId, feature.id);
            }
        });

        const sketches: SketchProjectionNode[] = sceneSketches.map((sketch, index) => ({
            id: sketch.id,
            displayName: this.resolveSketchDisplayName(sketch, index + 1, input.objectNameOverrides),
            entityType: sketch.entityType,
            order: sketch.order,
            hidden: input.hiddenObjectIds.has(sketch.id),
            suppressed: input.suppressedSketchIds.has(sketch.id),
            rolledBack: rolledBackSketchIds.has(sketch.id),
            editing: sketch.editing === true,
            consumedByFeatureId: consumingFeatureBySketchId.get(sketch.id),
            iconType: 'sketch',
            referenceWarning: sketch.referenceWarning
                ? structuredClone(sketch.referenceWarning)
                : undefined,
        }));

        const datumPlanes: DatumPlaneProjectionNode[] = sceneDatumPlanes.map((plane) => ({
            id: plane.id,
            displayName: plane.name.trim().length > 0 ? plane.name : `基准面${plane.id}`,
            order: plane.order,
            hidden: plane.hidden,
            rolledBack: rolledBackDatumPlaneIds.has(plane.id),
            iconType: 'plane',
        }));

        const datumAxes: DatumAxisProjectionNode[] = sceneDatumAxes.map((axis) => ({
            id: axis.id,
            displayName: axis.name.trim().length > 0 ? axis.name : `基准轴${axis.id}`,
            order: axis.order,
            hidden: axis.hidden,
            rolledBack: rolledBackDatumAxisIds.has(axis.id),
            iconType: 'axis',
        }));

        const datumPoints: DatumPointProjectionNode[] = sceneDatumPoints.map((point) => ({
            id: point.id,
            displayName: point.name.trim().length > 0 ? point.name : `基准点${point.id}`,
            order: point.order,
            hidden: point.hidden,
            rolledBack: rolledBackDatumPointIds.has(point.id),
            iconType: 'point',
        }));

        const currentResultBody = authoritativeBodies.find((object) => {
            const metadata = (object.metadata ?? {}) as Record<string, unknown>;
            return metadata.featureId === features.at(-1)?.id;
        }) ?? authoritativeBodies.at(-1);
        const currentResultBodyRefs = authoritativeBodies.map((object) => object.id);
        const rollbackTimelineItems = input.rollbackState?.timelineItems
            ?? createFeatureRollbackTimeline({
                features: features.map((feature, featureIndex) => {
                    const command = input.history.find((entry) => entry.id === feature.id);
                    const timestamp = Number(command?.timestamp);
                    return {
                        id: feature.id,
                        order: Number.isFinite(timestamp) ? timestamp : featureIndex,
                        sourceSketchId: feature.sourceSketchId,
                    };
                }),
                sketches,
                datumPlanes: datumPlanes.map((plane) => ({
                    id: plane.id,
                    order: plane.order,
                })),
                datumAxes: datumAxes.map((axis) => ({
                    id: axis.id,
                    order: axis.order,
                })),
                datumPoints: datumPoints.map((point) => ({
                    id: point.id,
                    order: point.order,
                })),
            });

        return {
            features,
            sketches,
            datumPlanes,
            datumAxes,
            datumPoints,
            currentResult: {
                bodyRefs: currentResultBody ? currentResultBodyRefs : [],
                authoritativeBodyCount: authoritativeBodies.length,
            },
            nextDisplayIndexByKind: {
                ...nextFeatureIndexByDisplayFamily,
                sketch: sketches.length + 1,
            },
            rollbackTimelineItems,
            rollbackCursorIndex: input.rollbackState?.cursorIndex ?? rollbackTimelineItems.length,
        };
    }

    /**
     * 构建设计树渲染模型。
     * @param document 投影文档。
     * @returns 渲染模型。
     */
    public buildRenderModel(document: FeatureProjectionDocument): FeatureTreeProjectionModel {
        const sketchNodeCountById = new Map<string, number>();
        const nodes: FeatureTreeProjectionNode[] = [];
        const featureById = new Map(document.features.map((feature) => [feature.id, feature]));
        const sketchById = new Map(document.sketches.map((sketch) => [sketch.id, sketch]));
        const datumPlaneById = new Map(document.datumPlanes.map((plane) => [plane.id, plane]));
        const datumAxisById = new Map(document.datumAxes.map((axis) => [axis.id, axis]));
        const datumPointById = new Map(document.datumPoints.map((point) => [point.id, point]));
        const rollbackCursorIndex = this.resolveRollbackCursorIndex(document);

        document.rollbackTimelineItems.forEach((timelineItem, timelineIndex) => {
            if (timelineIndex === rollbackCursorIndex) {
                nodes.push(this.createRollbackBarNode(rollbackCursorIndex, document.rollbackTimelineItems.length));
            }

            if (timelineItem.kind === FeatureRollbackTimelineItemKind.Feature) {
                const feature = featureById.get(timelineItem.id);
                if (!feature) {
                    return;
                }
                nodes.push(this.createFeatureRenderNode(feature, document, sketchNodeCountById, timelineIndex));
                return;
            }

            if (timelineItem.kind === FeatureRollbackTimelineItemKind.Sketch) {
                /**
                 * FeatureManager 时间线上的 Sketch 槽必须产出可落点树行：
                 * 1. 退回控制棒拖拽落点只认带 featureManagerIndex 的 DOM 行；
                 * 2. 静默跳过会造成棒下空洞，cursor 无法越过草图，图形隐藏后无法恢复；
                 * 3. 真正被特征吸收的草图不应再以 Sketch 槽存在，由 Controller 剪枝保证；
                 *    若槽位仍在，这里用场景草图或占位节点填行，绝不留空洞。
                 */
                const sketch = sketchById.get(timelineItem.id);
                const isRolledBack = sketch
                    ? sketch.rolledBack
                    : timelineIndex >= rollbackCursorIndex;
                sketchNodeCountById.set(
                    timelineItem.id,
                    (sketchNodeCountById.get(timelineItem.id) ?? 0) + 1,
                );
                nodes.push({
                    key: timelineItem.id,
                    label: sketch?.displayName ?? '草图',
                    nodeType: 'sketch',
                    iconType: sketch?.iconType ?? 'sketch',
                    refObjectId: timelineItem.id,
                    hidden: sketch?.hidden ?? false,
                    rolledBack: isRolledBack,
                    referenceWarning: sketch?.referenceWarning
                        ? structuredClone(sketch.referenceWarning)
                        : undefined,
                    featureManagerIndex: timelineIndex,
                });
                return;
            }

            if (timelineItem.kind === FeatureRollbackTimelineItemKind.DatumPlane) {
                const plane = datumPlaneById.get(timelineItem.id);
                if (!plane) {
                    return;
                }
                nodes.push({
                    key: `datum-plane-${plane.id}`,
                    label: plane.displayName,
                    nodeType: 'datum-plane',
                    iconType: plane.iconType,
                    refObjectId: plane.id,
                    hidden: plane.hidden,
                    rolledBack: plane.rolledBack,
                    featureManagerIndex: timelineIndex,
                });
                return;
            }

            if (timelineItem.kind === FeatureRollbackTimelineItemKind.DatumAxis) {
                const axis = datumAxisById.get(timelineItem.id);
                if (!axis) {
                    return;
                }
                nodes.push({
                    key: `datum-axis-${axis.id}`,
                    label: axis.displayName,
                    nodeType: 'datum-axis',
                    iconType: axis.iconType,
                    refObjectId: axis.id,
                    hidden: axis.hidden,
                    rolledBack: axis.rolledBack,
                    featureManagerIndex: timelineIndex,
                });
                return;
            }

            if (timelineItem.kind === FeatureRollbackTimelineItemKind.DatumPoint) {
                const point = datumPointById.get(timelineItem.id);
                if (!point) {
                    return;
                }
                nodes.push({
                    key: `datum-point-${point.id}`,
                    label: point.displayName,
                    nodeType: 'datum-point',
                    iconType: point.iconType,
                    refObjectId: point.id,
                    hidden: point.hidden,
                    rolledBack: point.rolledBack,
                    featureManagerIndex: timelineIndex,
                });
            }
        });

        if (rollbackCursorIndex === document.rollbackTimelineItems.length) {
            nodes.push(this.createRollbackBarNode(rollbackCursorIndex, document.rollbackTimelineItems.length));
        }

        return {
            nodes,
            sketchNodeCountById,
        };
    }

    /**
     * 解析退回控制棒插入索引。
     *
     * @param document 设计树投影文档。
     * @returns 控制棒插入索引；没有退回状态时位于末尾。
     */
    private resolveRollbackCursorIndex(document: FeatureProjectionDocument): number {
        return Math.max(0, Math.min(document.rollbackCursorIndex, document.rollbackTimelineItems.length));
    }

    /**
     * 创建退回控制棒渲染节点。
     *
     * @param cursorIndex 控制棒插入索引。
     * @param totalTimelineItemCount 完整 FeatureManager 退回序列数量。
     * @returns 退回控制棒节点。
     */
    private createRollbackBarNode(cursorIndex: number, totalTimelineItemCount: number): FeatureTreeProjectionNode {
        return {
            key: 'feature-rollback-bar',
            label: '退回控制棒',
            nodeType: 'rollback-bar',
            iconType: 'rollback-bar',
            rollbackCursorIndex: cursorIndex,
            featureManagerIndex: cursorIndex,
            totalTimelineItemCount,
            rolledBack: cursorIndex < totalTimelineItemCount,
        };
    }

    /**
     * 创建特征渲染节点。
     *
     * @param feature 特征投影节点。
     * @param document 设计树投影文档。
     * @param sketchNodeCountById 草图节点计数器。
     * @param featureManagerIndex FeatureManager 序列索引。
     * @returns 特征渲染节点。
     */
    private createFeatureRenderNode(
        feature: FeatureProjectionNode,
        document: FeatureProjectionDocument,
        sketchNodeCountById: Map<string, number>,
        featureManagerIndex: number,
    ): FeatureTreeProjectionNode {
        const sketch = feature.sourceSketchId
            ? document.sketches.find((entry) => entry.id === feature.sourceSketchId)
            : undefined;
        const children: FeatureTreeProjectionNode[] = [];

        if (sketch) {
            sketchNodeCountById.set(sketch.id, (sketchNodeCountById.get(sketch.id) ?? 0) + 1);
            children.push({
                key: `${feature.id}::${sketch.id}`,
                label: sketch.displayName,
                nodeType: 'sketch',
                iconType: sketch.iconType,
                refObjectId: sketch.id,
                hidden: sketch.hidden,
                /**
                 * 严格前缀编辑会回退消费 Feature，但当前源草图正是用户正在操作的对象。
                 * 只豁免该草图；其余未求值 Feature 下的吸收草图仍继承父节点的回退状态。
                 */
                rolledBack: feature.rolledBack && !sketch.editing,
                referenceWarning: sketch.referenceWarning
                    ? structuredClone(sketch.referenceWarning)
                    : undefined,
            });
        }

        return {
            key: feature.id,
            label: feature.displayName,
            nodeType: 'feature',
            iconType: feature.iconType,
            refObjectId: feature.id,
            bodyRefs: [...feature.bodyRefs],
            hidden: feature.hidden,
            rolledBack: feature.rolledBack,
            featureCommandIndex: feature.order,
            featureManagerIndex,
            children: children.length > 0 ? children : undefined,
        };
    }

    /**
     * 解析特征显示命名上下文。
     *
     * 设计说明：
     * 1. 投影层只关心“显示命名需要的最小领域信息”，不向 web-client 暴露具体命令参数结构；
     * 2. 圆角类型来自定义快照，是区分普通圆角与变化圆角的唯一可信来源；
     * 3. 非圆角特征返回空上下文，后续新增子类型命名时可在这里集中扩展。
     *
     * @param prototype 特征原型。
     * @returns 特征显示命名上下文。
     */
    private resolveFeatureDisplayNameContext(prototype: FeaturePrototype): FeatureDisplayNameContext {
        const snapshot = prototype.getDefinition().toSnapshot() as {
            commandParams?: {
                definition?: {
                    type?: unknown;
                };
            };
        };
        const filletType = snapshot.commandParams?.definition?.type;
        if (prototype.getFeatureKind() === 'fillet' && typeof filletType === 'string') {
            return { filletType };
        }
        return {};
    }

    /**
     * 解析命令内嵌的特征名称。
     *
     * 设计说明：
     * 1. 重命名覆盖仍由宿主侧 objectNameOverrides 优先处理；
     * 2. 命令参数中的 `name` 是 cad-core 可持久化的特征名称来源，投影层必须把它纳入同一命名策略；
     * 3. 这里只读取公共快照中的轻量字段，不让投影层依赖具体命令参数类型。
     *
     * @param prototype 特征原型。
     * @returns 命令内嵌名称；不存在时返回 null。
     */
    private resolveFeatureCommandName(prototype: FeaturePrototype): string | null {
        const snapshot = prototype.getDefinition().toSnapshot() as {
            commandParams?: {
                name?: unknown;
            };
        };
        return typeof snapshot.commandParams?.name === 'string'
            ? snapshot.commandParams.name
            : null;
    }

    /**
     * 判断对象是否为临时预览体。
     * @param object 几何对象。
     * @returns 是否为临时对象。
     */
    private isEphemeralPreviewObject(object: GeometryObject): boolean {
        const metadata = (object.metadata ?? {}) as Record<string, unknown>;
        return metadata.isEphemeralPreview === true
            || typeof metadata.previewOperation === 'string'
            || typeof metadata.previewObject === 'string';
    }

    /**
     * 解析草图显示名称。
     * @param sketch 草图输入。
     * @param index 序号。
     * @param overrides 覆盖表。
     * @returns 草图显示名称。
     */
    private resolveSketchDisplayName(
        sketch: SceneSketchProjectionInput,
        index: number,
        overrides: Map<string, string>,
    ): string {
        const overrideName = (overrides.get(sketch.id) ?? '').trim();
        if (overrideName.length > 0) {
            return overrideName;
        }
        const sketchName = (sketch.name ?? '').trim();
        if (sketchName.length > 0) {
            return sketchName;
        }
        return `草图${index}`;
    }

    /**
     * 解析特征图标类型。
     * @param featureKind 特征种类。
     * @returns 图标类型。
     */
    private resolveFeatureIconType(featureKind: string): string {
        switch (featureKind) {
            case 'chamfer':
                return 'chamfer-feature';
            case 'boss-extrude':
            case 'cut-extrude':
                return 'extrude-feature';
            case 'fillet':
                return 'fillet-feature';
            case 'rib':
                return 'rib-feature';
            case 'boss-revolve':
            case 'cut-revolve':
                return 'revolve-feature';
            default:
                return 'feature';
        }
    }
}

/**
 * 默认设计树投影构建器实例。
 */
export const featureTreeProjectionBuilder = new FeatureTreeProjectionBuilder();

/**
 * 构建设计树文档。
 * 说明：
 * 1. 该函数作为 projection 层的公共包装入口；
 * 2. 命名保持与 web-client 既有入口一致，便于后续替换本地实现；
 * 3. 内部统一委托给 cad-core 单一投影构建器，避免宿主层重复拼装。
 *
 * @param input 投影输入。
 * @returns 设计树文档。
 */
export function buildPartFeatureDocument(
    input: BuildPartFeatureDocumentInput,
): PartFeatureDocument {
    return featureTreeProjectionBuilder.buildDocument(input);
}

/**
 * 构建设计树渲染模型。
 * 说明：
 * 1. 该函数作为 projection 层的公共包装入口；
 * 2. 命名保持与宿主层既有消费入口一致；
 * 3. 内部统一委托给 cad-core 单一投影构建器，避免本地硬编码。
 *
 * @param document 设计树文档。
 * @returns 设计树渲染模型。
 */
export function buildFeatureTreeModel(
    document: PartFeatureDocument,
): FeatureTreeRenderModel {
    return featureTreeProjectionBuilder.buildRenderModel(document);
}
